import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import { TraceLink } from '@/features/traces/TraceLink';
import styles from './automations.module.css';

type Props = {
  runId: string;
  onClose: () => void;
};

export function AutomationRunDetailPanel({ runId, onClose }: Props) {
  const qc = useQueryClient();
  const detailQ = useQuery({
    queryKey: ['automation-run', runId],
    queryFn: () =>
      apiFetch<{
        run: {
          id: string;
          status: string;
          error_message: string | null;
          automation_name: string;
          trace_id: string | null;
        };
        steps: {
          step_order: number;
          step_type: string;
          status: string;
          output: Record<string, unknown> | null;
          error_message: string | null;
        }[];
      }>(`/api/automation-runs/${runId}`),
  });

  const retryM = useMutation({
    mutationFn: () =>
      apiFetch(`/api/automation-runs/${runId}/retry`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['automation-run', runId] });
    },
  });

  if (detailQ.isLoading) return <LoadingState label="Run laden…" />;
  if (!detailQ.data) return null;

  const { run, steps } = detailQ.data;

  return (
    <div className={styles.editorPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>
          Run — {run.automation_name} · <code>{run.status}</code>
        </h3>
        <Button type="button" variant="ghost" onClick={onClose}>
          Sluiten
        </Button>
      </div>
      {run.trace_id ? (
        <p>
          Hub trace: <TraceLink traceId={run.trace_id} />
        </p>
      ) : null}
      {run.error_message ? <p style={{ color: 'var(--color-danger, #b42318)' }}>{run.error_message}</p> : null}
      {run.status === 'failed' ? (
        <Button type="button" variant="secondary" disabled={retryM.isPending} onClick={() => retryM.mutate()}>
          Opnieuw proberen
        </Button>
      ) : null}
      <ol>
        {steps.map((s) => (
          <li key={s.step_order} style={{ marginBottom: '0.75rem' }}>
            <strong>
              {s.step_order}. {s.step_type}
            </strong>{' '}
            — {s.status}
            {s.output?.mail_message_id ? (
              <span>
                {' '}
                · draft/mail: <code>{String(s.output.mail_message_id)}</code>
              </span>
            ) : null}
            {s.output?.report_id ? (
              <span>
                {' '}
                · report: <code>{String(s.output.report_id)}</code>
              </span>
            ) : null}
            {s.output?.content ? (
              <pre className={styles.outputPre}>{String(s.output.content).slice(0, 1200)}</pre>
            ) : s.output ? (
              <pre className={styles.outputPre}>{JSON.stringify(s.output, null, 2).slice(0, 1200)}</pre>
            ) : null}
            {s.error_message ? <p>{s.error_message}</p> : null}
          </li>
        ))}
      </ol>
    </div>
  );
}
